import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  CircularProgress,
  IconButton,
  Button,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { FaComments } from 'react-icons/fa';
import { feedbackApi } from '../../../../services/api/feedbackApi';
import { meetingsApi } from '../../../../services/api/meetingsApi';
import { useAlert } from '../../../../contexts/AlertContext';
import { EmployeeAvatar } from '../../../common/EmployeeAvatar';
import EmptyState from '../../../common/EmptyState';
import EmployeeSelectStep from './EmployeeSelectStep';
import RelationTypeSelectStep from './RelationTypeSelectStep';
import { ReviewTypeBadge } from '../../../common/ReviewTypeBadge';
import {
  getReviewTypeColor,
  getReviewTypeTextColor,
  getReviewTypeText,
} from '../../../../constants/reviewTypes';
import { MEETING_REFERENCE_TYPES } from '../../../../constants/meetingReferenceTypes';

const STEPS = ['Select Employee', 'Select Feedback Result', 'Select Relation Type'];

/**
 * LinkFeedbackResultsModal - Wizard modal for linking Feedback Results to a meeting or talking point
 *
 * Step 1: Select a feedback assignment (completed ones only)
 * Step 2: Select the relation type
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal closes
 * @param {string} props.meetingId - The meeting ID to link to
 * @param {string} props.talkingPointId - Optional talking point ID to link to (if provided, links to talking point instead of meeting)
 * @param {Array} props.mandatoryAttendees - Array of mandatory attendees with employee info
 * @param {Function} props.onSuccess - Callback after successful linking
 */
const LinkFeedbackResultsModal = ({
  open,
  onClose,
  meetingId,
  talkingPointId,
  mandatoryAttendees = [],
  onSuccess,
}) => {
  const { showAlert } = useAlert();
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [assignmentsByEmployee, setAssignmentsByEmployee] = useState({});
  const [relationTypes, setRelationTypes] = useState([]);

  // Selected items from each step
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [selectedRelationType, setSelectedRelationType] = useState(null);

  // Track if step 0 was auto-skipped (single mandatory attendee)
  const [wasAutoSkipped, setWasAutoSkipped] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setSelectedEmployee(null);
      setSelectedAssignment(null);
      setSelectedRelationType(null);
      setAssignmentsByEmployee({});

      // Auto-skip employee selection if only one mandatory attendee
      if (mandatoryAttendees.length === 1) {
        const singleAttendee = mandatoryAttendees[0];
        setSelectedEmployee(singleAttendee.employee || singleAttendee);
        setActiveStep(1);
        setWasAutoSkipped(true);
        fetchAssignmentsForEmployee(singleAttendee.employee || singleAttendee);
      } else {
        setActiveStep(0);
        setWasAutoSkipped(false);
      }

      if (mandatoryAttendees.length > 0) {
        fetchRelationTypes();
      }
    }
  }, [open, mandatoryAttendees]);

  const fetchRelationTypes = async () => {
    try {
      const types = await meetingsApi.getMeetingRelationTypes();
      setRelationTypes(types || []);
    } catch (err) {
      console.error('Failed to fetch relation types:', err);
    }
  };

  const fetchAssignmentsForEmployee = async (employee) => {
    if (!employee?.id) {
      return;
    }

    setIsLoading(true);
    try {
      // Fetch completed feedback assignments for the selected employee using server-side filtering
      const response = await feedbackApi.getEmployeesAssignments({
        employeeId: employee.id,
        completedOnly: 'true',
        limit: 100, // Get up to 100 completed assignments
      });

      const assignments = response?.items || [];

      // Group by employee (will have only one employee)
      const assignmentsMap = {};
      if (assignments.length > 0) {
        assignmentsMap[employee.id] = {
          employee: employee,
          assignments: assignments,
        };
      }

      setAssignmentsByEmployee(assignmentsMap);
    } catch (err) {
      showAlert('Failed to load feedback assignments', 'error', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectEmployee = (employee) => {
    setSelectedEmployee(employee);
    setActiveStep(1);
    fetchAssignmentsForEmployee(employee);
  };

  const handleSelectAssignment = (assignment) => {
    setSelectedAssignment(assignment);
    setActiveStep(2);
  };

  const handleSelectRelationType = (relationType) => {
    setSelectedRelationType(relationType);
  };

  const handleBack = () => {
    if (activeStep === 2) {
      setActiveStep(1);
      setSelectedRelationType(null);
    } else if (activeStep === 1) {
      // Only go back to step 0 if there are multiple mandatory attendees
      if (mandatoryAttendees.length > 1) {
        setActiveStep(0);
        setSelectedEmployee(null);
        setSelectedAssignment(null);
        setAssignmentsByEmployee({});
      }
    }
  };

  const handleLink = async () => {
    if (!selectedAssignment || !selectedRelationType || isLinking) return;

    setIsLinking(true);
    try {
      const referenceData = {
        refType: MEETING_REFERENCE_TYPES.PERFORMANCE_FEEDBACK_RESULT,
        refEntity: selectedAssignment.id,
        meetingRelationTypeId: selectedRelationType.id,
      };

      // Use talking point API if talkingPointId is provided, otherwise use meeting API
      if (talkingPointId) {
        await meetingsApi.addTalkingPointReference(meetingId, talkingPointId, referenceData);
      } else {
        await meetingsApi.addMeetingReference(meetingId, referenceData);
      }

      showAlert('Feedback result linked successfully', 'success');
      onSuccess?.();
      onClose();
    } catch (err) {
      showAlert('Failed to link feedback result', 'error', err);
    } finally {
      setIsLinking(false);
    }
  };

  const hasAnyAssignments = Object.values(assignmentsByEmployee).some(
    data => data.assignments?.length > 0
  );

  const getReviewType = (assignment) => {
    return assignment.feedbackSession?.reviewType ||
           assignment.reviewers?.[0]?.reviewType;
  };

  const getSessionDate = (assignment) => {
    if (assignment.feedbackSession?.month && assignment.feedbackSession?.year) {
      return `Session of ${assignment.feedbackSession.month}/${assignment.feedbackSession.year}`;
    }
    if (assignment.assignedAt) {
      const date = new Date(assignment.assignedAt);
      return `Assigned ${date.getMonth() + 1}/${date.getFullYear()}`;
    }
    return null;
  };

  const renderStep0 = () => (
    <EmployeeSelectStep
      mandatoryAttendees={mandatoryAttendees}
      onSelectEmployee={handleSelectEmployee}
      description="Select an employee to view their feedback results"
      emptyStateDescription="Add mandatory attendees to the meeting to link feedback results"
    />
  );

  const renderStep1 = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!hasAnyAssignments) {
      return (
        <EmptyState
          title="No completed feedback results"
          description={`No completed feedback assignments found for ${selectedEmployee?.fullName || 'this employee'}`}
        />
      );
    }

    return (
      <Box>
        {Object.entries(assignmentsByEmployee).map(([employeeId, data]) => {
          if (!data.assignments?.length) return null;

          return (
            <Box key={employeeId} sx={{ mb: 3 }}>
              {/* Employee Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, pb: 1, borderBottom: '1px solid #E2E8F0' }}>
                <EmployeeAvatar employee={data.employee} size={32} />
                <Typography variant="subtitle2" fontWeight={600}>
                  {data.employee?.fullName || 'Unknown Employee'}
                </Typography>
              </Box>

              {/* Assignments List */}
              <List sx={{ p: 0 }}>
                {data.assignments.map((assignment) => {
                  const reviewType = getReviewType(assignment);
                  const sessionDate = getSessionDate(assignment);
                  const isSessionAssignment = !!assignment.feedbackSession;

                  return (
                    <ListItemButton
                      key={assignment.id}
                      onClick={() => handleSelectAssignment(assignment)}
                      sx={{
                        borderRadius: 1,
                        mb: 1,
                        border: '1px solid #E2E8F0',
                        '&:hover': {
                          backgroundColor: 'rgba(37, 99, 235, 0.05)',
                          borderColor: '#2563EB',
                        },
                      }}
                    >
                      <FaComments style={{ color: '#6B7280', marginRight: 12, fontSize: 16 }} />
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="body2" fontWeight={500}>
                              {isSessionAssignment
                                ? assignment.feedbackSession.name
                                : 'Individual Assignment'}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Chip
                                label={isSessionAssignment ? 'FEEDBACK SESSION' : 'INDIVIDUAL'}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '10px',
                                  backgroundColor: isSessionAssignment ? '#EBF5FF' : '#F3F4F6',
                                  color: isSessionAssignment ? '#1D4ED8' : '#6B7280',
                                  fontWeight: 600,
                                }}
                              />
                              {isSessionAssignment && assignment.feedbackSession.businessUnit?.name && (
                                <Typography variant="caption" color="text.secondary">
                                  {assignment.feedbackSession.businessUnit.name}
                                </Typography>
                              )}
                              {sessionDate && (
                                <Typography variant="caption" color="text.secondary">
                                  {sessionDate}
                                </Typography>
                              )}
                              {reviewType && (
                                <ReviewTypeBadge
                                  reviewType={reviewType}
                                  getReviewTypeColor={getReviewTypeColor}
                                  getReviewTypeTextColor={getReviewTypeTextColor}
                                  getReviewTypeText={getReviewTypeText}
                                />
                              )}
                            </Box>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderStep2 = () => {
    const reviewType = getReviewType(selectedAssignment);
    const sessionDate = getSessionDate(selectedAssignment);
    const isSessionAssignment = !!selectedAssignment?.feedbackSession;

    return (
      <RelationTypeSelectStep
        relationTypes={relationTypes}
        selectedRelationType={selectedRelationType}
        onSelectRelationType={handleSelectRelationType}
      >
        {/* Selected Assignment Summary */}
        <Box sx={{
          p: 2,
          mb: 3,
          backgroundColor: '#F8FAFC',
          borderRadius: 1,
          border: '1px solid #E2E8F0',
        }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Selected Feedback Result:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <FaComments style={{ color: '#6B7280', flexShrink: 0, marginTop: 2 }} />
            <Box>
              <Typography variant="body2" fontWeight={500}>
                {isSessionAssignment
                  ? selectedAssignment.feedbackSession.name
                  : 'Individual Assignment'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                {sessionDate && (
                  <Typography variant="caption" color="text.secondary">
                    {sessionDate}
                  </Typography>
                )}
                {reviewType && (
                  <ReviewTypeBadge
                    reviewType={reviewType}
                    getReviewTypeColor={getReviewTypeColor}
                    getReviewTypeTextColor={getReviewTypeTextColor}
                    getReviewTypeText={getReviewTypeText}
                  />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Employee: {selectedAssignment?.employee?.fullName}
              </Typography>
            </Box>
          </Box>
        </Box>
      </RelationTypeSelectStep>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '80vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography variant="h6">Link Feedback Results</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Stepper */}
      <Box sx={{ px: 3, pb: 2 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {STEPS.map((label, index) => {
            // Disable step 0 when it was auto-skipped (single mandatory attendee)
            const isDisabledStep = index === 0 && wasAutoSkipped;
            return (
              <Step
                key={label}
                completed={index < activeStep}
                sx={isDisabledStep ? {
                  '& .MuiStepLabel-iconContainer .Mui-completed': {
                    color: 'grey.400',
                  },
                } : {}}
              >
                <StepLabel>{label}</StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </Box>

      <DialogContent dividers sx={{ minHeight: 300 }}>
        {activeStep === 0 && renderStep0()}
        {activeStep === 1 && renderStep1()}
        {activeStep === 2 && renderStep2()}

        {isLinking && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {/* Show Back button on step 1 (if multiple attendees) and step 2 */}
        {((activeStep === 1 && mandatoryAttendees.length > 1) || activeStep === 2) && (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            disabled={isLinking}
          >
            Back
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={isLinking}>
          Cancel
        </Button>
        {activeStep === 2 && (
          <Button
            variant="contained"
            onClick={handleLink}
            disabled={!selectedRelationType || isLinking}
          >
            {isLinking ? 'Linking...' : 'Link'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default LinkFeedbackResultsModal;
